import type { ConversationalFormOption } from '@karehero/models';

import {
  AGE_BRACKET_ID,
  BENEFITS_CALCULATOR_STEPS,
  HAS_UNPAID_CARER_ID,
  HELP_TIME_ID,
  IS_DISABLED_ID,
  LOCATION_ID,
  SAVINGS_ID,
} from 'config/benefitsCalculatorV2Steps';
import type { Copy } from 'utils/person';

// The care profile questions onboarding v4 can ask, by id. Each is the v2
// benefits calculator's own, so an answer saves to both. Which are asked, and
// in what order, is the flow's to say (config/onboardingV4/flows).

/** Asked only once an earlier answer calls for it; the care form's own condition shape. */
export interface QuestionCondition {
  /** The question whose answer is looked at. */
  questionKey: string;
  operator: 'equal' | 'not-equal' | 'in';
  /** An option id, or the option ids any of which will do. */
  value: string | string[];
}

export interface TriageQuestion {
  /** The care profile question it answers. */
  id: string;
  /** The question, in the calculator's words. */
  ask: Copy;
  /** What is being looked for, while what they said is read. */
  lookingFor: Copy;
  /** The answer's label when it is checked at the end. */
  label: string;
  options: ConversationalFormOption[];
  condition?: QuestionCondition;
}

/** The calculator's own question, so both ask it in the same words. */
const calculatorStep = (id: string) => {
  const step = BENEFITS_CALCULATOR_STEPS.find((s) => s.id === id);
  if (!step) throw new Error(`No benefits calculator question ${id}`);
  return step;
};

/** Asked as `selfAsk` of themselves, and of someone else as `otherAsk` or else as the calculator asks it. */
const question = (
  id: string,
  {
    selfAsk,
    otherAsk,
    ...copy
  }: Pick<TriageQuestion, 'lookingFor' | 'label'> & {
    selfAsk: string;
    otherAsk?: string;
  },
): TriageQuestion => {
  const step = calculatorStep(id);
  return {
    id,
    ask: { self: selfAsk, other: otherAsk ?? step.ask },
    options: step.options ?? [],
    ...copy,
  };
};

export const TRIAGE_QUESTIONS: Readonly<Record<string, TriageQuestion>> = {
  [LOCATION_ID]: question(LOCATION_ID, {
    selfAsk:
      'Care and support work a little differently in each part of the UK. We need to know where you live, so our care experts can give you the right advice.',
    otherAsk:
      'Care and support work a little differently in each part of the UK. We need to know where {careRecipient} lives, so our care experts can give you the right advice.',
    lookingFor: { self: 'Where you live', other: 'Where they live' },
    label: 'Lives in',
  }),
  [AGE_BRACKET_ID]: question(AGE_BRACKET_ID, {
    selfAsk:
      'How old are you? The help our care experts can offer changes with age.',
    otherAsk:
      'How old is {careRecipient}? The help our care experts can offer changes with age.',
    lookingFor: { self: 'Your age', other: '{Their} age' },
    label: 'Age',
  }),
  [IS_DISABLED_ID]: question(IS_DISABLED_ID, {
    selfAsk: 'Do you have a disability or long-term condition?',
    lookingFor: { self: 'Your health', other: '{Their} health' },
    label: 'Disability or long-term condition',
  }),
  [HELP_TIME_ID]: question(HELP_TIME_ID, {
    selfAsk: 'Do you need help from someone through the day or night?',
    lookingFor: { self: 'The help you need', other: 'The help they need' },
    label: 'Needs help',
  }),
  [HAS_UNPAID_CARER_ID]: question(HAS_UNPAID_CARER_ID, {
    selfAsk: 'Is someone caring for you unpaid?',
    lookingFor: { self: 'Who helps you', other: 'Who helps them' },
    label: 'Unpaid carer',
  }),
  [SAVINGS_ID]: question(SAVINGS_ID, {
    selfAsk: 'Roughly, how much do you have in savings?',
    lookingFor: { self: 'Savings', other: 'Savings' },
    label: 'Savings',
  }),
};

/** The triage questions with these ids, in the order given; an unknown id is left out. */
export const triageQuestions = (ids: readonly string[]): TriageQuestion[] =>
  ids.flatMap((id) => TRIAGE_QUESTIONS[id] ?? []);
